import { useTranslation } from "react-i18next";
import { useAuth } from "@/providers/AuthProvider";
import { useUsers } from "@/hooks/useUsers";
import { CalendarIcon } from "lucide-react";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface KPIFilterState {
	userId: string | null;
	dateFrom: Date | null;
	dateTo: Date | null;
}

interface KPIFiltersProps {
	filters: KPIFilterState;
	onChange: (filters: KPIFilterState) => void;
}

type TimePeriod = "all" | "week" | "month" | "custom";

export function KPIFilters({ filters, onChange }: KPIFiltersProps) {
	const { t } = useTranslation();
	const { user } = useAuth();
	const { data: users } = useUsers();

	const timePeriod = getTimePeriod(filters);

	const handleUserChange = (value: string) => {
		onChange({
			...filters,
			userId: value === "all" ? null : value === "me" ? "me" : value,
		});
	};

	const handleTimePeriodChange = (value: string) => {
		const now = new Date();
		switch (value) {
			case "all":
				onChange({ ...filters, dateFrom: null, dateTo: null });
				break;
			case "week":
				onChange({
					...filters,
					dateFrom: startOfWeek(now, { weekStartsOn: 1 }),
					dateTo: endOfWeek(now, { weekStartsOn: 1 }),
				});
				break;
			case "month":
				onChange({
					...filters,
					dateFrom: startOfMonth(now),
					dateTo: endOfMonth(now),
				});
				break;
			case "custom":
				if (!filters.dateFrom)
					onChange({ ...filters, dateFrom: now, dateTo: now });
				break;
		}
	};

	return (
		<div className="flex flex-wrap items-center gap-3">
			{/* User filter */}
			<Select
				value={filters.userId === null ? "all" : filters.userId === "me" ? "me" : filters.userId}
				onValueChange={handleUserChange}
			>
				<SelectTrigger className="w-[200px]">
					<SelectValue placeholder={t("dashboard.kpi.filters.allUsers")} />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="all">{t("dashboard.kpi.filters.allUsers")}</SelectItem>
					{user && (
						<SelectItem value="me">{t("dashboard.kpi.filters.currentUser")}</SelectItem>
					)}
					{(users || []).map((u) => (
						<SelectItem key={u.id} value={u.id}>
							{[u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || u.id}
						</SelectItem>
					))}
				</SelectContent>
			</Select>

			{/* Time period filter */}
			<Select value={timePeriod} onValueChange={handleTimePeriodChange}>
				<SelectTrigger className="w-[180px]">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="all">{t("dashboard.kpi.filters.allTime")}</SelectItem>
					<SelectItem value="week">{t("dashboard.kpi.filters.thisWeek")}</SelectItem>
					<SelectItem value="month">{t("dashboard.kpi.filters.thisMonth")}</SelectItem>
					<SelectItem value="custom">{t("dashboard.kpi.filters.customRange")}</SelectItem>
				</SelectContent>
			</Select>

			{/* Custom date range pickers */}
			{timePeriod === "custom" && (
				<>
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									"w-[150px] justify-start text-left font-normal",
									!filters.dateFrom && "text-muted-foreground"
								)}
							>
								<CalendarIcon className="mr-2 h-4 w-4" />
								{filters.dateFrom ? format(filters.dateFrom, "PP") : t("dashboard.kpi.filters.from")}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0" align="start">
							<Calendar
								mode="single"
								selected={filters.dateFrom || undefined}
								onSelect={(date) => onChange({ ...filters, dateFrom: date || null })}
								initialFocus
							/>
						</PopoverContent>
					</Popover>

					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									"w-[150px] justify-start text-left font-normal",
									!filters.dateTo && "text-muted-foreground"
								)}
							>
								<CalendarIcon className="mr-2 h-4 w-4" />
								{filters.dateTo ? format(filters.dateTo, "PP") : t("dashboard.kpi.filters.to")}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0" align="start">
							<Calendar
								mode="single"
								selected={filters.dateTo || undefined}
								onSelect={(date) => onChange({ ...filters, dateTo: date || null })}
								initialFocus
							/>
						</PopoverContent>
					</Popover>
				</>
			)}
		</div>
	);
}

function getTimePeriod(filters: KPIFilterState): TimePeriod {
	if (!filters.dateFrom && !filters.dateTo) return "all";

	const now = new Date();
	const weekStart = startOfWeek(now, { weekStartsOn: 1 });
	const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
	const monthStart = startOfMonth(now);
	const monthEnd = endOfMonth(now);

	if (
		filters.dateFrom &&
		filters.dateTo &&
		Math.abs(filters.dateFrom.getTime() - weekStart.getTime()) < 60000 &&
		Math.abs(filters.dateTo.getTime() - weekEnd.getTime()) < 60000
	)
		return "week";

	if (
		filters.dateFrom &&
		filters.dateTo &&
		Math.abs(filters.dateFrom.getTime() - monthStart.getTime()) < 60000 &&
		Math.abs(filters.dateTo.getTime() - monthEnd.getTime()) < 60000
	)
		return "month";

	return "custom";
}
